import React from 'react';
import PropTypes from 'prop-types';
import { FiBell, FiX, FiInfo, FiCheckCircle, FiAlertCircle, FiBook } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';

const NotificationPanel = ({ notifications, onClose, isOpen }) => {
  const { isDark } = useTheme();

  const getTypeIcon = (type) => {
    switch (type) {
      case 'welcome':
        return <FiCheckCircle className="text-green-500" />;
      case 'info':
        return <FiInfo className="text-blue-500" />;
      case 'update':
        return <FiAlertCircle className="text-yellow-500" />;
      case 'quiz':
        return <FiBook className="text-purple-500" />;
      default:
        return <FiInfo className="text-gray-500" />;
    }
  };

  const getTypeBg = (type) => {
    switch (type) {
      case 'welcome':
        return isDark ? 'bg-green-500/10 border-l-2 border-green-500' : 'bg-green-50 border-l-2 border-green-500';
      case 'info':
        return isDark ? 'bg-blue-500/10 border-l-2 border-blue-500' : 'bg-blue-50 border-l-2 border-blue-500';
      case 'update':
        return isDark ? 'bg-yellow-500/10 border-l-2 border-yellow-500' : 'bg-yellow-50 border-l-2 border-yellow-500';
      case 'quiz':
        return isDark ? 'bg-purple-500/10 border-l-2 border-purple-500' : 'bg-purple-50 border-l-2 border-purple-500';
      default:
        return isDark ? 'bg-gray-500/10' : 'bg-gray-50';
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      let date;
      if (timestamp && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else if (typeof timestamp === 'number') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else {
        return 'Just now';
      }

      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      return date.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Notification Dropdown - Top Right */}
          <motion.div
            className={`fixed top-16 right-4 md:right-6 w-80 max-w-[calc(100vw-2rem)] rounded-2xl shadow-2xl z-50 overflow-hidden ${isDark
                ? 'bg-dark-secondary border border-white/10'
                : 'bg-white border border-gray-200'
              }`}
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <div className={`flex justify-between items-center px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'
              }`}>
              <h2 className={`text-base font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'
                }`}>
                <FiBell className="text-purple-500" />
                Notifications
                {notifications.length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-500 text-white rounded-full">
                    {notifications.length}
                  </span>
                )}
              </h2>
              <button
                onClick={onClose}
                className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                  }`}
                aria-label="Close notifications"
              >
                <FiX className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
              </button>
            </div>

            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length > 0 ? (
                <div className="p-2 space-y-2">
                  {notifications.map((notification) => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`p-3 rounded-xl ${getTypeBg(notification.type)}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {getTypeIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                            {notification.message}
                          </p>
                          <span className={`text-xs mt-1 block ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {formatTimestamp(notification.timestamp)}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="py-10 text-center">
                  <FiBell className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    No notifications yet
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className={`px-4 py-2 border-t ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
                <button className={`w-full text-center text-xs font-medium py-1.5 rounded-lg transition-colors ${isDark
                    ? 'text-purple-400 hover:bg-purple-500/10'
                    : 'text-purple-600 hover:bg-purple-50'
                  }`}>
                  Mark all as read
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

NotificationPanel.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      timestamp: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.string,
        PropTypes.number,
        PropTypes.instanceOf(Date)
      ]).isRequired,
      type: PropTypes.oneOf(['welcome', 'info', 'update', 'quiz']).isRequired
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool
};

NotificationPanel.defaultProps = {
  isOpen: true
};

export default NotificationPanel;